if (!customElements.get("bundle-builder")) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.minProducts = parseInt(this.dataset.minProducts) || 2;
        this.bundleCart = [];
        this.productsData = {};
        
        // Get stock threshold from first product card
        const firstCard = this.querySelector('.bundle-builder__product-card');
        this.stockThreshold = firstCard ? parseInt(firstCard.dataset.stockThreshold) || 5 : 5;
        
        // Get notification settings
        this.notificationDirection = this.dataset.notificationDirection || 'bottom';
        this.notificationEnter = this.dataset.notificationEnter || 'slide_up';
        this.notificationExit = this.dataset.notificationExit || 'slide_down';
        
        // Get discount settings
        this.discountPercentage = parseFloat(this.dataset.discountPercentage) || 0;
        this.discountPriceColor = this.dataset.discountPriceColor || '#ff0000';
        this.regularPriceFontSize = parseInt(this.dataset.regularPriceFontSize) || 16;
        this.discountPriceFontSize = parseInt(this.dataset.discountPriceFontSize) || 18;
        
        // Get translations from data attribute
        try {
          this.translations = JSON.parse(this.dataset.translations || '{}');
        } catch (e) {
          this.translations = {};
        }
        
        this.init();
      }

      init() {
        this.loadProductsData();
        this.bindEvents();
        this.loadBundleCartFromStorage();
        this.updateCartDisplay();
      }

      loadProductsData() {
        const productCards = this.querySelectorAll('.bundle-builder__product-card');
        productCards.forEach((card) => {
          const productId = card.dataset.productId;
          const variantId = card.dataset.variantId;
          const addButton = card.querySelector('.bundle-builder__add-button');
          
          // Get price from data attribute (already divided by 100 in Liquid)
          let price = 0;
          if (addButton && addButton.dataset.price) {
            price = parseFloat(addButton.dataset.price) || 0;
          } else {
            // Fallback: Try to get price from sale price first, then regular price
            const salePriceElement = card.querySelector('.bundle-builder__price .price-item--sale');
            const regularPriceElement = card.querySelector('.bundle-builder__price .price-item--regular');
            const priceElement = salePriceElement || regularPriceElement || card.querySelector('.bundle-builder__price .price-item');
            
            if (priceElement) {
              const priceText = priceElement.textContent.trim();
              // Match numbers with optional commas and decimal points
              const priceMatch = priceText.match(/[\d,]+\.?\d*/);
              if (priceMatch) {
                price = parseFloat(priceMatch[0].replace(/,/g, ''));
              }
            }
          }
          
          // Ensure price is not in cents (should already be divided by 100 from Liquid)
          // But double check - if price seems too high (like 1999 for €19.99), divide by 100
          if (price > 1000 && price < 1000000) {
            // Likely in cents, divide by 100
            price = price / 100;
          }

          // Get inventory quantity and policy
          const inventoryQuantity = addButton ? parseInt(addButton.dataset.inventoryQuantity) || 0 : 0;
          const inventoryPolicy = addButton ? addButton.dataset.inventoryPolicy || 'deny' : 'deny';

          this.productsData[productId] = {
            variantId: variantId,
            price: price,
            title: card.querySelector('.bundle-builder__product-title')?.textContent.trim() || '',
            image: card.querySelector('.bundle-builder__product-image img')?.src || '',
            inventoryQuantity: inventoryQuantity,
            inventoryPolicy: inventoryPolicy
          };
        });
      }

      bindEvents() {
        // Add to bundle buttons
        this.querySelectorAll('.bundle-builder__add-button').forEach((button) => {
          button.addEventListener('click', (e) => {
            this.handleAddToBundle(e.target);
          });
        });

        // Variant select changes
        this.querySelectorAll('.bundle-builder__variant-select').forEach((select) => {
          select.addEventListener('change', (e) => {
            this.handleVariantChange(e.target);
          });
        });

        // Clear bundle button
        const clearButton = this.querySelector(`#BundleClearButton-${this.sectionId}`);
        if (clearButton) {
          clearButton.addEventListener('click', () => {
            this.clearBundle();
          });
        }

        // Add to cart button
        const addToCartButton = this.querySelector(`#BundleAddToCartButton-${this.sectionId}`);
        if (addToCartButton) {
          addToCartButton.addEventListener('click', () => {
            this.addBundleToCart();
          });
        }

        // Remove item buttons (will be added dynamically)
        this.addEventListener('click', (e) => {
          if (e.target.classList.contains('bundle-builder__remove-button')) {
            this.removeFromBundle(e.target.dataset.productId);
          }
        });
      }

      handleAddToBundle(button) {
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId || this.productsData[productId]?.variantId;
        
        if (!variantId) {
          this.showError('Please select a variant');
          return;
        }

        const productData = this.productsData[productId];
        if (!productData) {
          this.showError('Product not found');
          return;
        }

        // Get current inventory data from button
        const inventoryQuantity = parseInt(button.dataset.inventoryQuantity) || 0;
        const inventoryPolicy = button.dataset.inventoryPolicy || 'deny';

        // Check if product already in bundle
        const existingIndex = this.bundleCart.findIndex(item => item.productId === productId && item.variantId === variantId);
        
        let newQuantity = 1;
        if (existingIndex > -1) {
          newQuantity = this.bundleCart[existingIndex].quantity + 1;
        }

        // Check inventory limits (only if inventory is managed by Shopify)
        if (inventoryPolicy === 'deny' && inventoryQuantity > 0) {
          // Check current quantity in bundle cart
          const currentQuantityInCart = existingIndex > -1 ? this.bundleCart[existingIndex].quantity : 0;
          const totalQuantityAfterAdd = currentQuantityInCart + 1;
          
          if (totalQuantityAfterAdd > inventoryQuantity) {
            this.showError(this.getTranslation('quantity_exceeded') || 'Cannot add more than available quantity');
            return;
          }
        }

        if (existingIndex > -1) {
          this.bundleCart[existingIndex].quantity = newQuantity;
        } else {
          this.bundleCart.push({
            productId: productId,
            variantId: variantId,
            quantity: 1,
            title: productData.title,
            price: productData.price,
            image: productData.image,
            inventoryQuantity: inventoryQuantity,
            inventoryPolicy: inventoryPolicy
          });
        }

        this.saveBundleCartToStorage();
        this.updateCartDisplay();
        
        // Show notification when item is added to bundle cart
        const addedMessage = productData.title;
        console.log('[Bundle Builder] Item added to bundle, showing notification:', addedMessage);
        this.showNotification(addedMessage);
      }

      handleVariantChange(select) {
        const productId = select.dataset.productId;
        const variantId = select.value;
        const card = select.closest('.bundle-builder__product-card');
        const option = select.options[select.selectedIndex];
        
        // Get inventory data from option
        const inventoryQuantity = parseInt(option.dataset.inventoryQuantity) || 0;
        const inventoryPolicy = option.dataset.inventoryPolicy || 'deny';
        
        // Update the other select (mobile/desktop) to match
        const allSelects = card?.querySelectorAll(`.bundle-builder__variant-select[data-product-id="${productId}"]`);
        if (allSelects) {
          allSelects.forEach(otherSelect => {
            if (otherSelect !== select) {
              otherSelect.value = variantId;
            }
          });
        }
        
        // Update the add button's variant ID and inventory data
        const addButton = this.querySelector(`.bundle-builder__add-button[data-product-id="${productId}"]`);
        if (addButton) {
          addButton.dataset.variantId = variantId;
          addButton.dataset.inventoryQuantity = inventoryQuantity;
          addButton.dataset.inventoryPolicy = inventoryPolicy;
          
          // Update availability
          if (option.disabled) {
            addButton.disabled = true;
            addButton.textContent = addButton.dataset.soldOutText || 'Sold out';
          } else {
            addButton.disabled = false;
            addButton.textContent = addButton.dataset.addText || 'Add to bundle';
          }
        }

        // Update inventory display
        const inventoryElement = card?.querySelector('.bundle-builder__product-inventory');
        if (inventoryElement) {
          inventoryElement.setAttribute('data-variant-id', variantId);
          const quantityElement = inventoryElement.querySelector('.bundle-builder__inventory-quantity');
          const threshold = parseInt(card?.dataset.stockThreshold) || this.stockThreshold;
          
          if (inventoryPolicy === 'continue') {
            inventoryElement.innerHTML = `<span class="bundle-builder__inventory-text">${this.getTranslation('unlimited') || 'Unlimited'}</span>`;
          } else if (inventoryQuantity > 0) {
            // Apply threshold display logic
            let displayQuantity = inventoryQuantity;
            if (inventoryQuantity > threshold) {
              displayQuantity = `${threshold}+`;
            }
            
            if (quantityElement) {
              quantityElement.textContent = displayQuantity;
              quantityElement.dataset.quantity = inventoryQuantity;
              quantityElement.dataset.displayQuantity = displayQuantity;
            } else {
              inventoryElement.innerHTML = `<span class="bundle-builder__inventory-text">${this.getTranslation('available_quantity') || 'Available quantity'}: <strong class="bundle-builder__inventory-quantity" data-quantity="${inventoryQuantity}" data-display-quantity="${displayQuantity}">${displayQuantity}</strong></span>`;
            }
          } else {
            inventoryElement.innerHTML = `<span class="bundle-builder__inventory-text bundle-builder__inventory-text--out">${this.getTranslation('sold_out') || 'Sold out'}</span>`;
          }
        }

        // Update price if available in the card
        if (card) {
          const salePriceElement = card.querySelector('.bundle-builder__price .price-item--sale');
          const regularPriceElement = card.querySelector('.bundle-builder__price .price-item--regular');
          const priceElement = salePriceElement || regularPriceElement || card.querySelector('.bundle-builder__price .price-item');
          
          if (priceElement) {
            const priceText = priceElement.textContent.trim();
            const priceMatch = priceText.match(/[\d,]+\.?\d*/);
            if (priceMatch) {
              const newPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
              if (this.productsData[productId]) {
                this.productsData[productId].price = newPrice;
              }
            }
          }
        }

        // Update product data
        if (this.productsData[productId]) {
          this.productsData[productId].variantId = variantId;
          this.productsData[productId].inventoryQuantity = inventoryQuantity;
          this.productsData[productId].inventoryPolicy = inventoryPolicy;
          if (variantPrice > 0) {
            this.productsData[productId].price = variantPrice;
          }
        }
      }

      removeFromBundle(productId) {
        this.bundleCart = this.bundleCart.filter(item => item.productId !== productId);
        this.saveBundleCartToStorage();
        this.updateCartDisplay();
      }

      clearBundle() {
        const modal = document.querySelector(`#BundleClearModal-${this.sectionId}`);
        if (!modal) {
          // Fallback to confirm if modal doesn't exist
          if (confirm('Are you sure you want to clear the bundle?')) {
            this.bundleCart = [];
            this.saveBundleCartToStorage();
            this.updateCartDisplay();
          }
          return;
        }

        // Show modal using modal-dialog's show method if available
        if (typeof modal.show === 'function') {
          modal.show();
        } else {
          modal.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }

        // Handle modal actions
        const cancelButton = modal.querySelector('[data-action="cancel"]');
        const confirmButton = modal.querySelector('[data-action="confirm"]');
        const closeButton = modal.querySelector(`#ModalClose-BundleClear-${this.sectionId}`);

        const hideModal = () => {
          if (typeof modal.hide === 'function') {
            modal.hide();
          } else {
            modal.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
          }
        };

        const handleConfirm = () => {
          this.bundleCart = [];
          this.saveBundleCartToStorage();
          this.updateCartDisplay();
          hideModal();
        };

        // Remove old event listeners by cloning buttons
        if (cancelButton && confirmButton && closeButton) {
          const cancelClone = cancelButton.cloneNode(true);
          const confirmClone = confirmButton.cloneNode(true);
          const closeClone = closeButton.cloneNode(true);
          
          cancelButton.parentNode.replaceChild(cancelClone, cancelButton);
          confirmButton.parentNode.replaceChild(confirmClone, confirmButton);
          closeButton.parentNode.replaceChild(closeClone, closeButton);

          cancelClone.addEventListener('click', hideModal);
          closeClone.addEventListener('click', hideModal);
          confirmClone.addEventListener('click', handleConfirm);
        }

        // Close on backdrop click (only if clicking the modal itself, not content)
        const handleBackdropClick = (e) => {
          if (e.target === modal) {
            hideModal();
            modal.removeEventListener('click', handleBackdropClick);
          }
        };
        modal.addEventListener('click', handleBackdropClick);
      }

      updateCartDisplay() {
        const cartItemsContainer = this.querySelector(`#BundleCartItems-${this.sectionId}`);
        const cartCountElement = this.querySelector('.bundle-builder__cart-count');
        const totalPriceElement = this.querySelector('.bundle-builder__cart-total-price');
        const addToCartButton = this.querySelector(`#BundleAddToCartButton-${this.sectionId}`);
        const emptyMessage = cartItemsContainer?.querySelector('.bundle-builder__cart-empty');

        if (!cartItemsContainer) return;

        const itemCount = this.bundleCart.reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = this.bundleCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        // Calculate discounted prices
        const discountMultiplier = (100 - this.discountPercentage) / 100;
        const discountedTotalPrice = totalPrice * discountMultiplier;
        const hasDiscount = this.discountPercentage > 0;

        // Update count
        if (cartCountElement) {
          cartCountElement.textContent = itemCount;
          cartCountElement.dataset.itemCount = itemCount;
        }

        // Update total price with discount
        if (totalPriceElement) {
          const currencySymbol = totalPriceElement.textContent.match(/[^\d.,]+/)?.[0] || this.getCurrencySymbol();
          if (hasDiscount) {
            totalPriceElement.innerHTML = `
              <span class="bundle-builder__cart-total-price-regular" style="font-size: ${this.regularPriceFontSize}px; text-decoration: line-through; opacity: 0.6; margin-right: 0.5rem;">
                ${currencySymbol}${totalPrice.toFixed(2)}
              </span>
              <span class="bundle-builder__cart-total-price-discount" style="font-size: ${this.discountPriceFontSize}px; color: ${this.discountPriceColor};">
                ${currencySymbol}${discountedTotalPrice.toFixed(2)}
              </span>
            `;
          } else {
            totalPriceElement.textContent = `${currencySymbol}${totalPrice.toFixed(2)}`;
          }
          totalPriceElement.dataset.totalPrice = hasDiscount ? discountedTotalPrice : totalPrice;
        }

        // Update add to cart button state
        if (addToCartButton) {
          addToCartButton.disabled = itemCount < this.minProducts;
        }

        // Render cart items
        if (itemCount === 0) {
          if (emptyMessage) {
            emptyMessage.style.display = 'block';
          }
          cartItemsContainer.innerHTML = '<p class="bundle-builder__cart-empty">' + (this.getTranslation('cart_empty') || emptyMessage?.textContent || 'Your bundle is empty') + '</p>';
        } else {
          if (emptyMessage) {
            emptyMessage.style.display = 'none';
          }
          
          cartItemsContainer.innerHTML = this.bundleCart.map((item, index) => {
            const maxQuantity = item.inventoryQuantity || 999;
            const showMaxWarning = item.inventoryPolicy === 'deny' && item.inventoryQuantity > 0 && item.quantity >= maxQuantity;
            
            // Calculate discounted price for this item
            const itemTotalPrice = item.price * item.quantity;
            const itemDiscountedPrice = hasDiscount ? itemTotalPrice * discountMultiplier : itemTotalPrice;
            const currencySymbol = this.getCurrencySymbol();
            
            return `
            <div class="bundle-builder__cart-item" data-product-id="${item.productId}" data-variant-id="${item.variantId}">
              <div class="bundle-builder__cart-item-image">
                <img src="${item.image}" alt="${item.title}" loading="lazy">
              </div>
              <div class="bundle-builder__cart-item-info">
                <h4 class="bundle-builder__cart-item-title">${item.title}</h4>
                <div class="bundle-builder__cart-item-details">
                  <span class="bundle-builder__cart-item-quantity">
                    Qty: ${item.quantity}
                    ${item.inventoryPolicy === 'deny' && item.inventoryQuantity > 0 ? ` / ${maxQuantity}` : ''}
                  </span>
                  <span class="bundle-builder__cart-item-price">
                    ${hasDiscount ? `
                      <span class="bundle-builder__cart-item-price-regular" style="font-size: ${this.regularPriceFontSize}px; text-decoration: line-through; opacity: 0.6; margin-right: 0.5rem;">
                        ${currencySymbol}${itemTotalPrice.toFixed(2)}
                      </span>
                      <span class="bundle-builder__cart-item-price-discount" style="font-size: ${this.discountPriceFontSize}px; color: ${this.discountPriceColor};">
                        ${currencySymbol}${itemDiscountedPrice.toFixed(2)}
                      </span>
                    ` : `
                      <span style="font-size: ${this.regularPriceFontSize}px;">
                        ${currencySymbol}${itemTotalPrice.toFixed(2)}
                      </span>
                    `}
                  </span>
                </div>
                ${showMaxWarning ? `<p class="bundle-builder__cart-item-warning">${this.getTranslation('max_quantity_reached')}</p>` : ''}
              </div>
              <button 
                type="button" 
                class="bundle-builder__remove-button" 
                data-product-id="${item.productId}"
                aria-label="Remove ${item.title} from bundle"
              >
                ×
              </button>
            </div>
          `;
          }).join('');
        }
      }

      async addBundleToCart() {
        if (this.bundleCart.length === 0) {
          this.showError('Bundle is empty');
          return;
        }

        if (this.bundleCart.reduce((sum, item) => sum + item.quantity, 0) < this.minProducts) {
          this.showError(`Please add at least ${this.minProducts} products to complete the bundle`);
          return;
        }

        const addToCartButton = this.querySelector(`#BundleAddToCartButton-${this.sectionId}`);
        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.classList.add('loading');
          addToCartButton.textContent = 'Adding...';
        }

        try {
          // Get cart notification element
          const cartNotification = document.querySelector('cart-notification');
          const sectionsToRender = cartNotification && cartNotification.getSectionsToRender 
            ? cartNotification.getSectionsToRender().map(section => section.id)
            : ['cart-notification-product', 'cart-notification-subtotal', 'cart-notification-count', 'cart-notification-discount'];

          // Add items one by one to cart using theme's method
          let lastResult = null;
          for (let i = 0; i < this.bundleCart.length; i++) {
            const item = this.bundleCart[i];
            const formData = new FormData();
            formData.append('id', item.variantId);
            formData.append('quantity', item.quantity);
            
            // Add sections for cart notification (only for last item to update drawer)
            if (i === this.bundleCart.length - 1 && sectionsToRender.length > 0) {
              formData.append('sections', sectionsToRender.join(','));
              formData.append('sections_url', window.location.pathname);
            }

            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = formData;

            const response = await fetch(window.routes.cart_add_url, config);
            const result = await response.json();

            if (result.status) {
              throw new Error(result.description || 'Failed to add item to cart');
            }
            
            lastResult = result;
          }

          // Success - clear bundle and show success message
          console.log('[Bundle Builder] Bundle added to cart successfully, showing notification');
          this.showSuccess('Bundle added to cart successfully!');
          const successMessage = this.getTranslation('bundle_added_success') || 'Bundle added to cart successfully!';
          console.log('[Bundle Builder] Calling showNotification with message:', successMessage);
          this.showNotification(successMessage);
          this.bundleCart = [];
          this.saveBundleCartToStorage();
          this.updateCartDisplay();

          // Update cart notification and open drawer using theme's method
          if (cartNotification && lastResult && lastResult.sections) {
            if (cartNotification.renderContents) {
              // Use theme's renderContents which will automatically open the drawer
              cartNotification.renderContents(lastResult);
            } else if (cartNotification.open) {
              // Fallback: just open the drawer
              cartNotification.open();
            }
          } else if (cartNotification && cartNotification.open) {
            // If no sections, just open the drawer
            cartNotification.open();
          }

          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:updated'));

        } catch (error) {
          this.showError(error.message || 'Failed to add bundle to cart');
        } finally {
          if (addToCartButton) {
            addToCartButton.disabled = false;
            addToCartButton.classList.remove('loading');
            // Use translation instead of hardcoded text
            addToCartButton.textContent = this.getTranslation('add_to_cart');
          }
        }
      }


      getCurrencySymbol() {
        const totalPriceElement = this.querySelector('.bundle-builder__cart-total-price');
        if (totalPriceElement) {
          const match = totalPriceElement.textContent.match(/[^\d.,]+/);
          return match ? match[0] : '';
        }
        return '';
      }

      getTranslation(key) {
        // First try to get from data attribute translations
        if (this.translations && this.translations[key]) {
          return this.translations[key];
        }
        // Fallback to English
        const fallbacks = {
          'quantity_exceeded': 'Cannot add more than available quantity',
          'max_quantity_reached': 'Maximum available quantity reached',
          'available_quantity': 'Available quantity',
          'unlimited': 'Unlimited',
          'in_stock': 'In stock',
          'sold_out': 'Sold out',
          'bundle_added_success': 'Bundle added to cart successfully!',
          'add_to_cart': 'Add to cart'
        };
        return fallbacks[key] || key;
      }

      showError(message) {
        const errorElement = this.querySelector(`#BundleCartError-${this.sectionId}`);
        const messageContainer = this.querySelector(`#BundleCartMessage-${this.sectionId}`);
        const successElement = this.querySelector(`#BundleCartSuccess-${this.sectionId}`);

        if (errorElement) {
          errorElement.textContent = message;
        }
        if (successElement) {
          successElement.textContent = '';
        }
        if (messageContainer) {
          messageContainer.hidden = false;
        }

        setTimeout(() => {
          if (messageContainer) {
            messageContainer.hidden = true;
          }
        }, 5000);
      }

      showSuccess(message) {
        const successElement = this.querySelector(`#BundleCartSuccess-${this.sectionId}`);
        const messageContainer = this.querySelector(`#BundleCartMessage-${this.sectionId}`);
        const errorElement = this.querySelector(`#BundleCartError-${this.sectionId}`);

        if (successElement) {
          successElement.textContent = message;
        }
        if (errorElement) {
          errorElement.textContent = '';
        }
        if (messageContainer) {
          messageContainer.hidden = false;
        }

        setTimeout(() => {
          if (messageContainer) {
            messageContainer.hidden = true;
          }
        }, 5000);
      }

      saveBundleCartToStorage() {
        try {
          localStorage.setItem(`bundle-cart-${this.sectionId}`, JSON.stringify(this.bundleCart));
        } catch (e) {
          console.error('Failed to save bundle cart to storage', e);
        }
      }

      loadBundleCartFromStorage() {
        try {
          const stored = localStorage.getItem(`bundle-cart-${this.sectionId}`);
          if (stored) {
            this.bundleCart = JSON.parse(stored);
          }
        } catch (e) {
          console.error('Failed to load bundle cart from storage', e);
        }
      }

      showNotification(message) {
        console.log('[Bundle Builder] ========== showNotification CALLED ==========');
        console.log('[Bundle Builder] Parameters:', { message, sectionId: this.sectionId });
        console.log('[Bundle Builder] Looking for element: #BundleNotification-' + this.sectionId);
        
        // Notification is outside bundle-builder, so query from document
        const notification = document.querySelector(`#BundleNotification-${this.sectionId}`);
        
        if (!notification) {
          console.error('[Bundle Builder] ❌ Notification element NOT FOUND for section:', this.sectionId);
          console.error('[Bundle Builder] Searched selector:', `#BundleNotification-${this.sectionId}`);
          console.error('[Bundle Builder] Available notification elements:', document.querySelectorAll('[id^="BundleNotification"]'));
          return;
        }
        
        console.log('[Bundle Builder] ✅ Notification element FOUND:', notification);
        console.log('[Bundle Builder] Notification element ID:', notification.id);
        console.log('[Bundle Builder] Notification element classes:', notification.className);

        const textElement = notification.querySelector('.bundle-builder__notification-text');
        if (textElement) {
          textElement.textContent = message;
        }

        // Ensure notification is visible
        notification.style.display = 'block';
        
        // Set direction class
        notification.className = `bundle-builder__notification bundle-builder__notification--${this.notificationDirection}`;
        
        // Remove all animation classes
        notification.classList.remove(
          'bundle-builder__notification--slide-up-enter',
          'bundle-builder__notification--slide-down-enter',
          'bundle-builder__notification--fade-in-enter',
          'bundle-builder__notification--slide-left-enter',
          'bundle-builder__notification--slide-right-enter',
          'bundle-builder__notification--slide-down-exit',
          'bundle-builder__notification--slide-up-exit',
          'bundle-builder__notification--fade-out-exit',
          'bundle-builder__notification--slide-left-exit',
          'bundle-builder__notification--slide-right-exit'
        );

        // Map animation names based on direction and convert underscore to hyphen
        let enterAnimation = this.notificationEnter;
        if ((this.notificationDirection === 'left' || this.notificationDirection === 'right') && enterAnimation === 'slide_up') {
          enterAnimation = 'slide_left';
        } else if ((this.notificationDirection === 'left' || this.notificationDirection === 'right') && enterAnimation === 'slide_down') {
          enterAnimation = 'slide_right';
        }
        
        // Convert underscore to hyphen for CSS class names
        enterAnimation = enterAnimation.replace(/_/g, '-');

        // Add enter animation class
        notification.classList.add(`bundle-builder__notification--${enterAnimation}-enter`);
        console.log('[Bundle Builder] Enter animation:', enterAnimation, 'Class:', `bundle-builder__notification--${enterAnimation}-enter`);
        notification.classList.add('bundle-builder__notification--show');
        console.log('[Bundle Builder] Notification classes added:', notification.className);
        console.log('[Bundle Builder] Notification computed styles:', {
          display: window.getComputedStyle(notification).display,
          visibility: window.getComputedStyle(notification).visibility,
          opacity: window.getComputedStyle(notification).opacity
        });
        
        // Force reflow to trigger animation
        void notification.offsetHeight;
        console.log('[Bundle Builder] Notification should be visible now');
        console.log('[Bundle Builder] Notification final state:', {
          display: notification.style.display,
          className: notification.className,
          hidden: notification.hidden
        });
        console.log('[Bundle Builder] ========== showNotification COMPLETE ==========');

        // Close button handler
        const closeButton = notification.querySelector('.bundle-builder__notification-close');
        if (closeButton) {
          const handleClose = () => {
            this.hideNotification();
            closeButton.removeEventListener('click', handleClose);
          };
          closeButton.addEventListener('click', handleClose);
        }

        // Auto hide after 5 seconds
        setTimeout(() => {
          this.hideNotification();
        }, 5000);
      }

      hideNotification() {
        console.log('[Bundle Builder] hideNotification called:', { sectionId: this.sectionId });
        const notification = document.querySelector(`#BundleNotification-${this.sectionId}`);
        if (!notification) {
          console.warn('[Bundle Builder] Notification element not found for hide:', this.sectionId);
          return;
        }

        // Remove enter animation, add exit animation
        notification.classList.remove(
          'bundle-builder__notification--slide-up-enter',
          'bundle-builder__notification--slide-down-enter',
          'bundle-builder__notification--fade-in-enter',
          'bundle-builder__notification--slide-left-enter',
          'bundle-builder__notification--slide-right-enter'
        );

        // Map exit animation names based on direction and convert underscore to hyphen
        let exitAnimation = this.notificationExit;
        if ((this.notificationDirection === 'left' || this.notificationDirection === 'right') && exitAnimation === 'slide_up') {
          exitAnimation = 'slide_left';
        } else if ((this.notificationDirection === 'left' || this.notificationDirection === 'right') && exitAnimation === 'slide_down') {
          exitAnimation = 'slide_right';
        }
        
        // Convert underscore to hyphen for CSS class names
        exitAnimation = exitAnimation.replace(/_/g, '-');

        notification.classList.add(`bundle-builder__notification--${exitAnimation}-exit`);
        console.log('[Bundle Builder] Exit animation:', exitAnimation, 'Class:', `bundle-builder__notification--${exitAnimation}-exit`);

        // Remove show class after animation
        setTimeout(() => {
          notification.classList.remove('bundle-builder__notification--show');
          notification.classList.remove(`bundle-builder__notification--${exitAnimation}-exit`);
          notification.style.display = 'none';
          notification.style.visibility = 'hidden';
          notification.style.opacity = '0';
        }, 300);
      }
    }
  );
}

